import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { motion } from "framer-motion";
import { ExternalLink } from "lucide-react";
import { useInView } from "./AnimatedComponents";

interface ResumeItemProps {
  year: string;
  title: string;
  subTitle: string;
  textArr: string[];
  slug?: string;
}

const ResumeItem: React.FC<ResumeItemProps> = ({
  year,
  title,
  subTitle,
  textArr,
  slug,
}) => {
  const { ref, inView } = useInView(0.15);

  return (
    <ResumeItemStyled
      ref={ref}
      as={motion.div}
      initial={{ opacity: 0, x: -20 }}
      animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
      transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
    >
      <motion.div
        className="timeline-dot"
        initial={{ scale: 0 }}
        animate={inView ? { scale: 1 } : { scale: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
      />
      <div className="timeline-content">
        {year && <span className="date">{year}</span>}
        <h5>{title}</h5>
        <h6 className="subtitle">{subTitle}</h6>
        {textArr.map((text, index) => (
          <p key={index} className="description">
            {text}
          </p>
        ))}
        {slug && (
          <Link to={`/role/${slug}`} className="details-btn">
            <ExternalLink size={14} />
            View Role Details
          </Link>
        )}
      </div>
    </ResumeItemStyled>
  );
};

const ResumeItemStyled = styled.div`
  display: flex;
  gap: 1.5rem;
  position: relative;
  padding-bottom: 2.5rem;

  &:last-child {
    padding-bottom: 0;
  }

  .timeline-dot {
    position: relative;
    flex-shrink: 0;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    border: 2px solid var(--primary-color);
    background: var(--background-dark-color);
    margin-top: 6px;
    z-index: 1;

    &::after {
      content: "";
      position: absolute;
      left: 50%;
      top: 100%;
      transform: translateX(-50%);
      width: 2px;
      height: calc(100% + 2.5rem - 12px);
      background: var(--border-color);
    }
  }

  &:last-child .timeline-dot::after {
    display: none;
  }

  .timeline-content {
    flex: 1;
    min-width: 0;

    .date {
      display: inline-block;
      font-size: 0.75rem;
      font-weight: 600;
      color: var(--primary-color);
      background: var(--background-light-color-2);
      padding: 0.2rem 0.75rem;
      border-radius: 100px;
      margin-bottom: 0.5rem;
      letter-spacing: 0.02em;
    }

    h5 {
      font-size: 1.2rem;
      font-weight: 700;
      margin-bottom: 0.2rem;
      line-height: 1.3;
      color: var(--white-color);
    }

    .details-btn {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      margin-top: 0.85rem;
      padding: 0.5rem 1.15rem;
      font-size: 0.8rem;
      font-weight: 600;
      font-family: inherit;
      color: var(--primary-color);
      border: 1px solid var(--primary-color);
      border-radius: 100px;
      background: transparent;
      text-decoration: none;
      letter-spacing: 0.02em;
      transition: all 0.25s ease;

      svg {
        flex-shrink: 0;
        transition: transform 0.25s ease;
      }

      &:hover {
        background: var(--primary-color);
        color: white;

        svg {
          transform: translateX(2px) translateY(-2px);
        }
      }
    }

    .subtitle {
      font-size: 0.95rem;
      font-weight: 500;
      color: var(--primary-color-light);
      padding-bottom: 0.6rem;
    }

    .description {
      font-size: 0.85rem;
      line-height: 1.7;
      color: var(--font-light-color);
      margin-bottom: 0.3rem;
      white-space: pre-line;
    }
  }

  @media screen and (max-width: 480px) {
    gap: 1rem;

    .timeline-content {
      h5 {
        font-size: 1rem;
      }
      .subtitle {
        font-size: 0.85rem;
      }
      .description {
        font-size: 0.8rem;
      }
    }
  }
`;

export default ResumeItem;
